import { captureFailure } from '@root/private/js/common/sentry';

/**
 * 풀스크린 진입 실패 감시.
 *
 * JW Player 는 풀스크린 실패를 알려주지 않는다. 제공하는 이벤트는
 * `fullscreen: { fullscreen: boolean }` 상태 변경 알림 하나뿐이고,
 * `setFullscreen(true)` 는 void 를 반환한다. 브라우저 API 레벨에서도
 * iOS 의 webkitEnterFullscreen 은 실패 시 조용히 no-op 이다.
 *
 * 따라서 "요청했는데 타임아웃 내에 fullscreen 이벤트가 오지 않음" 을
 * 실패 신호로 사용한다.
 */

const FULLSCREEN_TIMEOUT_MS = 1000;

export type FullscreenSource = 'playOption' | 'postMessage';

// jwplayer 타입을 import 하면 IE 에서 에러가 나므로 필요한 만큼만 선언한다.
interface FullscreenCapablePlayer {
  setFullscreen(state: boolean): void;
  on(event: 'fullscreen', callback: (param: { fullscreen: boolean }) => void): unknown;
}

let entered = false;
let listenerBound = false;
const reported: Record<string, boolean> = {};

/**
 * 진단 컨텍스트. iPad 가 desktop UA 로 보고하는 문제 때문에 UA 단독으로는
 * 원인 판별이 불가능하므로 실제 API 지원 여부를 함께 수집한다.
 */
const collectContext = (): Record<string, unknown> => {
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitFullscreenEnabled?: boolean;
  };
  const video = document.querySelector('video') as
    | (HTMLVideoElement & { webkitDisplayingFullscreen?: boolean })
    | null;

  return {
    userAgent: navigator.userAgent,
    isIframe: window.self !== window.top,
    maxTouchPoints: navigator.maxTouchPoints,
    fullscreenEnabled: !!(doc.fullscreenEnabled || doc.webkitFullscreenEnabled),
    hasFullscreenElement: !!(
      doc.fullscreenElement || doc.webkitFullscreenElement
    ),
    // iOS 는 document.fullscreenElement 를 채우지 않으므로 별도로 확인한다.
    videoDisplayingFullscreen: video
      ? !!video.webkitDisplayingFullscreen
      : null,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

/**
 * JW 의 fullscreen 이벤트를 구독한다. 멱등이며 요청 시 자동으로 호출되므로
 * 호출자가 배선 순서를 신경 쓸 필요가 없다.
 *
 * (iframe-handler 는 player-settings 보다 먼저 배선될 수 있어서, 구독을
 *  ready 훅에 두면 부모 프레임의 이른 fullscreenOn 요청에서 오탐이 난다)
 */
export const setupFullscreenReport = (
  playerInstance: FullscreenCapablePlayer,
): void => {
  if (listenerBound) return;
  listenerBound = true;

  playerInstance.on('fullscreen', ({ fullscreen }) => {
    entered = fullscreen;
  });
};

/**
 * 풀스크린 진입을 요청하고, 타임아웃 내에 상태 변경이 없으면 실패로 보고한다.
 *
 * 고트래픽 플레이어이므로 source 별로 페이지 로드당 1회만 보고한다.
 * (반복 클릭이 이슈를 뒤덮는 것을 막는다)
 */
export const requestFullscreenWithReport = (
  playerInstance: FullscreenCapablePlayer,
  source: FullscreenSource,
): void => {
  setupFullscreenReport(playerInstance);

  entered = false;
  playerInstance.setFullscreen(true);

  window.setTimeout(() => {
    if (entered || reported[source]) return;

    reported[source] = true;
    captureFailure('JW fullscreen enter failed', {
      tags: { feature: 'fullscreen', fullscreenSource: source },
      extra: {
        timeoutMs: FULLSCREEN_TIMEOUT_MS,
        ...collectContext(),
      },
    });
  }, FULLSCREEN_TIMEOUT_MS);
};

/** 테스트 전용. 모듈 스코프 상태를 초기화한다. */
export const __resetForTest = (): void => {
  entered = false;
  listenerBound = false;
  Object.keys(reported).forEach((key) => delete reported[key]);
};
